import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Network from '../../../models/Network.js';

import FilterListElement from '../list_elements/FilterListElement.js';
import LoadingListElement from '../list_elements/LoadingListElement.js';
import GraphApiFatigue from '../graphics/GraphApiFatigue.js';
import './Module.css';

const key = process.env.REACT_APP_CLIENT_ACCESS_KEY;
const URLS = require("../../../urls.js");

const StatusCodes = require('../../../status-codes/status-codes.js')
const ErrorCodes = require('../../../error-codes/error-codes.js')

const FatigueGraphsModule = ({user, isSmallScreen}) => {

    const [jsonData, setJsonData] = useState(null);
    const [filterDict, setFilterDict] = useState(null);
    const [medicsData, setMedicsData] = useState(null);
    
    const graphParams = {
        width: 700,
        height: 400
    }

    // function that gets multiple reports with fatigue analysis data from the server
    const getSeveralReportsFatigueFiltered = async (desiredNumber, reportOffset, filterDict) => {
        
        const url = URLS.GET_SEVERAL_REPORTS_FATIGUE_FILTERED;
        const dict = {
            uid: user.uid,
            number_of_reports: desiredNumber,
            offset: reportOffset,
            filter_dict: filterDict,
            CLIENT_ACCESS_KEY: key
        };

        let responseData;

        await Network.post(url, dict, (response) => {

            responseData = response.data;

            const extractedErrorCode = responseData.toString();
            const status = response.status

            if (Network.checkValidStatusAndResponse(response)) {
                // do something

            } else if (status == StatusCodes.INTERNAL_SERVER_ERROR) {
                console.log("Server Error")

            } else {
                console.log("Unknown Error")
            }
        });

        return responseData;
    };

    const getAllAgencyMedics = async (agencyId) => {
        const url = URLS.GET_MEDICS_FOR_USER;
        const dict = {
            uid: user.uid,
            agency_id: agencyId,
            CLIENT_ACCESS_KEY: key
        };

        let responseData;

        await Network.post(url, dict, (response) => {
            responseData = response.data;

            const extractedErrorCode = responseData.toString();
            const status = response.status

            if (Network.checkValidStatusAndResponse(response)) {
                // do something
            } else if (status == StatusCodes.INTERNAL_SERVER_ERROR) {
                console.log("Server Error")
            } else {
                console.log("Unknown Error")
            }
        });

        return responseData;
    };

    // get all medics from the server
    useEffect(() => {
        getAllAgencyMedics(user.uid)
            .then((data) => {
                setMedicsData(data);
            })
            .catch((error) => {
                console.log('Error:', error);
            });
    }, []);

    // Process data chronologically to calculate fatigue indicators
    const processFatigueData = (rawData) => {
        if (!rawData || !Array.isArray(rawData) || rawData.length === 0) return [];

        // Sort reports chronologically by creation_timestamp
        const sortedReports = [...rawData].sort((a, b) => {
            const timeA = new Date(a.creation_timestamp).getTime();
            const timeB = new Date(b.creation_timestamp).getTime();
            return timeA - timeB;
        });

        // Track state for each medic (authors and drivers)
        const medicState = {}; // medicId -> { shiftStartTime, totalDrivingTime, lastReportTime }

        // Process each report chronologically
        const processedReports = sortedReports.map(report => {
            // Parse medic IDs from author, response_driver, and transport_driver (can be comma-separated)
            const authorIds = report.author ? report.author.split(',').map(id => id.trim()).filter(id => id) : [];
            const responseDriverIds = report.response_driver ? report.response_driver.split(',').map(id => id.trim()).filter(id => id) : [];
            const transportDriverIds = report.transport_driver ? report.transport_driver.split(',').map(id => id.trim()).filter(id => id) : [];
            
            // Get driving times in milliseconds
            const drivingToScene = report.driving_to_scene_time || 0;
            const sceneToDestination = report.scene_to_destination_time || 0;
            const totalDrivingTime = drivingToScene + sceneToDestination;
            
            const reportTimestamp = new Date(report.creation_timestamp).getTime();
            const EIGHT_HOURS_MS = 8 * 60 * 60 * 1000;
            
            // Collect all unique medic IDs involved in this report (authors and drivers)
            const allMedicIds = new Set([...authorIds, ...responseDriverIds, ...transportDriverIds]);
            
            // Update shift tracking for all medics involved in this report
            allMedicIds.forEach(medicId => {
                // Initialize medic state if needed
                if (!medicState[medicId]) {
                    medicState[medicId] = {
                        shiftStartTime: reportTimestamp,
                        totalDrivingTime: 0,
                        lastReportTime: reportTimestamp
                    };
                }
                
                const medic = medicState[medicId];
                const timeSinceLastReport = reportTimestamp - medic.lastReportTime;
                
                // Check if this is a new shift (gap > 8 hours)
                if (timeSinceLastReport > EIGHT_HOURS_MS) {
                    // New shift - reset counters
                    medic.shiftStartTime = reportTimestamp;
                    medic.totalDrivingTime = 0;
                }
                
                // Add driving time if this medic was a driver (response or transport)
                if (responseDriverIds.includes(medicId) || transportDriverIds.includes(medicId)) {
                    medic.totalDrivingTime += totalDrivingTime;
                }
                
                // Update last report time for this medic
                medic.lastReportTime = reportTimestamp;
            });
            
            // Calculate fatigue indicators for the author medic (use first author if multiple)
            let authorTotalShiftTime = null;
            let authorTotalDrivingTime = null;
            
            if (authorIds.length > 0) {
                const authorId = authorIds[0]; // Use primary author
                const medic = medicState[authorId];
                
                if (medic) {
                    // Calculate total time on current shift (in minutes)
                    const shiftDuration = reportTimestamp - medic.shiftStartTime;
                    authorTotalShiftTime = shiftDuration / 60000; // Convert to minutes
                    
                    // Get total driving time accumulated so far in this shift (in minutes)
                    authorTotalDrivingTime = medic.totalDrivingTime / 60000; // Convert to minutes
                }
            }
            
            // Return report with fatigue indicators
            return {
                ...report,
                author_total_shift_time_minutes: authorTotalShiftTime,
                author_total_driving_time_minutes: authorTotalDrivingTime
            };
        });
        
        return processedReports;
    };

    // get report analytics data from the server
    useEffect(() => {
        // Only load data when user has selected a date filter
        if (filterDict && (filterDict.lowerDate || filterDict.upperDate)) {
            getSeveralReportsFatigueFiltered(5000, 0, filterDict) // max 5000 reports, no offset
                .then((data) => {
                    // Process the data to calculate fatigue indicators
                    const processedData = processFatigueData(data);
                    setJsonData(processedData); // TODO: add protection against API failure
                    console.log('Setting processed fatigue data:', processedData);
                })
                .catch((error) => {
                    console.log('Error:', error);
                });
        } else {
            // Clear data if no date filter is set
            setJsonData(null);
        }
    }, [filterDict]);

    
    return (
        <div className={isSmallScreen ? "standard-module-screen-small" : "standard-module"}>
            <FilterListElement
                medicsData={medicsData}
                filterState={
                    {
                        date: [],
                        read: ["both"],
                        finished: ["both"]
                    }
                }
                dictUpdateHandler={setFilterDict}
                showClearButton={false}
                filterDropdownsVisible={{
                    date: true,
                    medics: true,
                    typeTags: true,
                    // measures: false - not included for fatigue graphs
                }}
            />
            {!filterDict || (!filterDict.lowerDate && !filterDict.upperDate) ? (
                <div style={{ padding: '20px', textAlign: 'center' }}>
                    <h4 className='module-subtitle'>Select a date range above to visualize fatigue analysis data.</h4>
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-around' }}>
                    {jsonData ? (
                        <div>
                            <GraphApiFatigue 
                                data={jsonData} 
                                graphParams={graphParams}
                            />
                        </div>
                    ) : (
                        <LoadingListElement height={400} backgroundColor="white"/>
                    )}
                </div>
            )}
        </div>
    );
}

export default FatigueGraphsModule;

